import React, { useEffect, useMemo } from 'react'
import { Table, Spin } from 'antd'
import { useLazyQuery } from '@apollo/client'
import { GET_DOMAINS } from '../gql/registroHorasGQLignore'
import { DOMAIN_CONFIG } from '../shared/domainConfig'
import { buildDomainLists } from '../shared/buildDomainLists'
import { generalToObjectiveType, objectiveToSpecificType } from '../shared/activityTypeMapping'
import { normalizeKey } from '../../shared/verteoffice'

const DOMAINS_QUERY = GET_DOMAINS()

interface ReferenceRow {
  key: string
  generalActivity: string
  objective: string
  specificActivity: string
  generalActivityRowSpan: number
  objectiveRowSpan: number
}

function buildReferenceRows(domainDataList: Record<string, string[]>): ReferenceRow[] {
  const rows: ReferenceRow[] = []
  const generalActivities = domainDataList['generalActivityList'] ?? []

  for (const generalActivityName of generalActivities) {
    const objectiveDomainType = generalToObjectiveType[normalizeKey(generalActivityName)]
    if (!objectiveDomainType) continue

    const objConfig = DOMAIN_CONFIG.find(
      c => normalizeKey(c.type) === normalizeKey(objectiveDomainType)
    )
    if (!objConfig) continue

    const objectives = domainDataList[objConfig.listKey] ?? []
    const generalActivityRows: ReferenceRow[] = []

    for (const objectiveName of objectives) {
      const specificType = objectiveToSpecificType[normalizeKey(objectiveName)]
      const specConfig = specificType
        ? DOMAIN_CONFIG.find(c => normalizeKey(c.type) === normalizeKey(specificType))
        : undefined
      const specificActivities = specConfig
        ? (domainDataList[specConfig.listKey] ?? [])
        : []

      const items = specificActivities.length > 0 ? specificActivities : ['—']

      items.forEach((specificActivity, idx) => {
        generalActivityRows.push({
          key: `${generalActivityName}-${objectiveName}-${specificActivity}-${idx}`,
          generalActivity: generalActivityName,
          objective: objectiveName,
          specificActivity,
          generalActivityRowSpan: 0,
          objectiveRowSpan: idx === 0 ? items.length : 0,
        })
      })
    }

    if (generalActivityRows.length > 0) {
      generalActivityRows[0].generalActivityRowSpan = generalActivityRows.length
    }

    rows.push(...generalActivityRows)
  }

  return rows
}

const ActivityReferenceTable: React.FC = () => {
  const [loadDomains, { data: domainsData, loading }] = useLazyQuery(DOMAINS_QUERY, {
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    loadDomains()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const rows = useMemo<ReferenceRow[]>(() => {
    if (!domainsData) return []
    return buildReferenceRows(buildDomainLists(domainsData, DOMAIN_CONFIG))
  }, [domainsData])

  if (loading) return <Spin style={{ display: 'block', padding: '32px 0', textAlign: 'center' }} />

  const columns = [
    {
      title: 'Actividad General',
      dataIndex: 'generalActivity',
      width: '25%',
      onCell: (_: ReferenceRow, index?: number) => ({
        rowSpan: rows[index ?? 0]?.generalActivityRowSpan ?? 1,
      }),
      render: (value: string) => <strong>{value}</strong>,
    },
    {
      title: 'Objetivo',
      dataIndex: 'objective',
      width: '35%',
      onCell: (_: ReferenceRow, index?: number) => ({
        rowSpan: rows[index ?? 0]?.objectiveRowSpan ?? 1,
      }),
    },
    {
      title: 'Actividad Específica',
      dataIndex: 'specificActivity',
      width: '40%',
    },
  ]

  return (
    <Table
      dataSource={rows}
      columns={columns}
      pagination={false}
      size='small'
      bordered
      scroll={{ y: 480 }}
    />
  )
}

export default ActivityReferenceTable
